/**
 * Value Generation Display Component
 * 
 * Displays three value generators (Customer, Business, Stakeholder)
 * with assessment ranks and value ratios. Used in operator UI role.
 */

import React from 'react';
import { CardSectionProps } from '../../../types/compositeCards';
import { 
  StarOutlined,
  RiseOutlined,
  DollarOutlined,
  UserOutlined,
  TeamOutlined,
  BankOutlined
} from '@ant-design/icons';

interface ValueGenerator {
  type: 'customer' | 'business' | 'stakeholder';
  label: string;
  icon: React.ReactNode;
  assessmentScore: number; // 1-10
  valueCreated: number;
  costToDeliver: number;
  valueRatio: number; // valueCreated / costToDeliver
  color: string;
}

export const ValueGenerationDisplay: React.FC<CardSectionProps> = ({
  section,
  card,
  onAction
}) => {
  const { data } = card;

  // Get value generators from artifact data (nested in data.artifact)
  const artifact = (data as any).artifact || data;
  const generators: ValueGenerator[] = artifact.valueGenerators || [
    {
      type: 'customer',
      label: 'Customer Value',
      icon: <UserOutlined />,
      assessmentScore: 7,
      valueCreated: 150000,
      costToDeliver: 50000,
      valueRatio: 3.0,
      color: '#3b82f6'
    },
    {
      type: 'business',
      label: 'Business Value',
      icon: <DollarOutlined />,
      assessmentScore: 6,
      valueCreated: 100000,
      costToDeliver: 75000,
      valueRatio: 1.33,
      color: '#10b981'
    },
    {
      type: 'stakeholder',
      label: 'Stakeholder Value',
      icon: <TeamOutlined />,
      assessmentScore: 5,
      valueCreated: 50000,
      costToDeliver: 40000,
      valueRatio: 1.25,
      color: '#8b5cf6'
    }
  ];

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getRatioColor = (ratio: number): string => {
    if (ratio >= 2.5) return '#10b981'; // Green
    if (ratio >= 1.5) return '#f59e0b'; // Orange
    return '#ef4444'; // Red
  };

  const getValueLabels = (type: string): { created: string; cost: string } => {
    switch (type) {
      case 'business':
        return { created: 'Revenue', cost: 'Expenses' };
      case 'stakeholder':
        return { created: 'Return', cost: 'Investment' };
      case 'customer':
      default:
        return { created: 'Value Created', cost: 'Cost to Deliver' };
    }
  };

  const handleIncreaseValue = (type: string) => {
    // Trigger action to increase value
    onAction?.(`increase-${type}-value`, {
      valueType: type,
      advanceScene: true
    });
  };

  return (
    <div style={{
      padding: '16px',
      borderTop: '1px solid #e5e7eb',
      borderBottom: '1px solid #e5e7eb'
    }}>
      <h3 style={{
        margin: '0 0 16px 0',
        fontSize: '15px',
        fontWeight: '600',
        color: '#374151',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <RiseOutlined style={{ color: '#10b981' }} />
        Value Generation
      </h3>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}>
        {generators.map((generator) => (
          <div
            key={generator.type}
            style={{
              padding: '16px',
              backgroundColor: generator.color + '08',
              border: `2px solid ${generator.color}30`,
              borderRadius: '12px'
            }}
          >
            {/* Generator Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  backgroundColor: generator.color,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px'
                }}>
                  {generator.icon}
                </div>
                <div>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    {generator.label}
                  </div>
                </div>
              </div>

              {/* Assessment Score */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <StarOutlined style={{ color: '#f59e0b', fontSize: '14px' }} />
                <span style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: generator.color
                }}>
                  {generator.assessmentScore}
                </span>
                <span style={{
                  fontSize: '13px',
                  color: '#6b7280'
                }}>
                  / 10
                </span>
              </div>
            </div>

            {/* Value Metrics */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '12px',
              marginBottom: '12px'
            }}>
              {/* Value Created (or Revenue/Return) */}
              <div style={{
                padding: '8px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  fontWeight: '500'
                }}>
                  {getValueLabels(generator.type).created}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#10b981'
                }}>
                  {formatCurrency(generator.valueCreated)}
                </div>
              </div>

              {/* Cost to Deliver (or Expenses/Investment) */}
              <div style={{
                padding: '8px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  fontWeight: '500'
                }}>
                  {getValueLabels(generator.type).cost}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#ef4444'
                }}>
                  {formatCurrency(generator.costToDeliver)}
                </div>
              </div>

              {/* Value Ratio */}
              <div style={{
                padding: '8px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: `2px solid ${getRatioColor(generator.valueRatio)}`
              }}>
                <div style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  fontWeight: '500'
                }}>
                  Value Ratio
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: getRatioColor(generator.valueRatio),
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {generator.valueRatio.toFixed(2)}x
                  <RiseOutlined style={{ fontSize: '14px' }} />
                </div>
              </div>
            </div>

            {/* Action Button */}
            <button
              onClick={() => handleIncreaseValue(generator.type)}
              style={{
                width: '100%',
                padding: '10px 16px',
                backgroundColor: generator.color,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <RiseOutlined />
              Increase {generator.label}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

